// The search box in the header ("/" focuses it; Cmd / Ctrl + K is the AI assistant only): pages by name, and records -
// equipment, inventory, crew by name, To Do, documents. Rights as everywhere else: a kind is searched only when the
// login may view that module (can) and the vessel has it (hasFeature); the database rules still apply to every read.
// Crew: names and position only (never select=* on crew - src/lib/crewPrivacy.js). To Do: the items the To Do page
// shows this login (own, assigned to it, unowned, or on a system list). A hit opens the record on its page.
import { isSystemList } from './todoLists.js'
import { ARCHIVABLE, missingArchive } from './archive.js'

// q -> safe inside a PostgREST or=(col.ilike.*q*): no , ( ) * % \ and quotes
export const cleanQuery = q => String(q || '').replace(/[,()*%\\"':]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60)
const ors = (cols, q) => cols.map(c => c + '.ilike.*' + q + '*').join(',')

// kind -> permission key (permModules.js), plan key (features.js), how to read it and how to open a hit
export const RECORD_KINDS = [
  { kind: 'Equipment', perm: 'equipment', feature: 'equipment', table: 'equipment', cols: 'id,uid,name,location', search: ['name', 'uid', 'location'],
    label: r => r.name || r.uid, sub: r => [r.uid, r.location].filter(Boolean).join(' · '), open: r => ({ page: 'equipment', q: r.name || r.uid, id: r.id }) },
  { kind: 'Inventory', perm: 'inventory', feature: 'inventory', table: 'inventory', cols: 'id,part_uid,part_name,part_number,location', search: ['part_name', 'part_number', 'part_uid'],
    label: r => r.part_name || r.part_uid, sub: r => [r.part_number, r.location].filter(Boolean).join(' · '), open: r => ({ page: 'inventory', q: r.part_name || r.part_uid, id: r.id }) },
  { kind: 'Crew', perm: 'crew', feature: 'crew', table: 'crew', cols: 'id,first_name,last_name,name,position', search: ['first_name', 'last_name', 'name'],
    label: r => r.name || [r.first_name, r.last_name].filter(Boolean).join(' '), sub: r => r.position || '', open: r => ({ page: 'crew', store: { hc_open_crew_id: String(r.id), hc_open_crew_tab: 'info' } }) },
  { kind: 'To Do', perm: 'todo', feature: 'todo', table: 'todo_items', cols: 'id,text,list_id,done,created_by,assignee', search: ['text'],
    label: r => r.text, sub: r => (r.done ? 'done' : 'open'), open: r => ({ page: 'todo', store: { hc_open_todo: String(r.id) } }) },
  { kind: 'Document', perm: 'documents', feature: 'docs', table: 'documents', cols: 'id,title,document_type,expiry_date', search: ['title', 'doc_id'],
    label: r => r.title, sub: r => [r.document_type, r.expiry_date ? 'expires ' + r.expiry_date : ''].filter(Boolean).join(' · '), open: r => ({ page: 'docs', store: { hc_open_doc: r.title || '' } }) },
]

export const searchableKinds = ({ can, hasFeature }) =>
  RECORD_KINDS.filter(k => (!can || can(k.perm, 'read')) && (!hasFeature || hasFeature(k.feature)))

// db: the supabase client; who: { cid, me (crew name / email), can, hasFeature }; per kind at most `per` hits
export async function searchRecords(db, q, who, per = 4) {
  const s = cleanQuery(q)
  if (s.length < 2 || !who || !who.cid) return []
  const kinds = searchableKinds(who)
  const out = await Promise.all(kinds.map(async k => {
    try {
      let rows
      if (k.table === 'todo_items') {
        // To Do items have no client_id: through this vessel's lists, and only what the To Do page shows this login
        const { data: ls } = await db.from('todo_lists').select('id,name').eq('client_id', who.cid)
        if (!ls || !ls.length) return []
        const sys = new Set(ls.filter(l => isSystemList(l.name)).map(l => l.id))
        const { data } = await db.from('todo_items').select(k.cols).in('list_id', ls.map(l => l.id)).or(ors(k.search, s)).limit(25)
        rows = (data || []).filter(i => i.created_by === who.me || i.assignee === who.me || !i.created_by || sys.has(i.list_id))
      } else {
        // archived equipment is not offered (src/lib/archive.js); before sql/archive.sql the filter is dropped
        const make = () => db.from(k.table).select(k.cols).eq('client_id', who.cid).or(ors(k.search, s)).limit(per)
        let { data, error } = ARCHIVABLE.includes(k.table) ? await make().is('archived_at', null) : await make()
        if (error && ARCHIVABLE.includes(k.table) && missingArchive(error.message)) ({ data, error } = await make())
        if (error) return []
        rows = data || []
      }
      return rows.slice(0, per).map(r => ({ kind: k.kind, id: String(r.id), label: String(k.label(r) || '').slice(0, 80), sub: String(k.sub(r) || '').slice(0, 80), open: k.open(r) })).filter(h => h.label)
    } catch (e) { return [] }
  }))
  return out.flat()
}

// open a hit: the same keys the pages already read (hc_open_q for the list pages, hc_open_doc, hc_open_crew_id, hc_open_todo)
export function openHit(hit, storage = globalThis.localStorage) {
  const o = hit.open || {}
  try {
    if (o.q) storage.setItem('hc_open_q', JSON.stringify({ page: o.page, q: o.q, id: o.id || null }))
    Object.entries(o.store || {}).forEach(([k, v]) => storage.setItem(k, v))
    storage.setItem('hc_page', o.page)
  } catch (e) {}
  return o.page
}

// "/" focuses the search unless the user is typing somewhere (or holds a modifier)
export const isSearchShortcut = e => !!e && e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target)
export const isTyping = el => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || !!el.isContentEditable)
